import { useEffect, useState } from "react";
import CommonPagination from "../../Common/CommonPagination";
import { FaInfoCircle, FaSearch, FaSpinner } from "react-icons/fa";
import CommonContainer from "../Container/CommonContainer";
import toast from "react-hot-toast";
import * as NPSFeedbackReportService from "../../Services/MHL_Reports/NPSFeedbackReportService";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";
import { formatDate, formatVisitDate } from "../../Common/CommonVariables";
import Popover from "../../Common/Popper";

const NPSFeedbackReport = () => {
  const [npsFeedback, setNpsFeedback] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [loading, setLoading] = useState(false);

  const today = new Date();

  const [selectedMonth, setSelectedMonth] = useState(
    (today.getMonth() + 1).toString().padStart(2, "0"),
  );

  const [selectedYear, setSelectedYear] = useState(
    today.getFullYear().toString(),
  );

  const itemsPerPage = 15;

  const getNpsFeedbackByMonth = () => {
    setLoading(true);
    const formattedDate = `${selectedYear}-${selectedMonth}-01`;

    NPSFeedbackReportService.GetNpsFeedbackByMonth(formattedDate)
      .then(({ data }) => {
        if (data.IsSuccess) {
          setNpsFeedback(data?.Data);
        } else {
          setNpsFeedback([]);
        }
      })
      .catch(() => {
        setNpsFeedback([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getNpsFeedbackByMonth();
  }, [selectedMonth, selectedYear]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredRegistrationData = npsFeedback.filter((data) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      data.Partner_Code?.toLowerCase().includes(lowerSearch) ||
      data.Partner_Name?.toLowerCase().includes(lowerSearch) ||
      data.Feedback?.toLowerCase().includes(lowerSearch)
    );
  });

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentRegistrationData = filteredRegistrationData.slice(
    indexOfFirst,
    indexOfLast,
  );

  const handleDownloadReport = async () => {
    try {
      setIsDownloadingReport(true);

      if (filteredRegistrationData.length === 0) {
        toast.error("No data available to download");
        setIsDownloadingReport(false);
        return;
      }

      const dataForExcel = filteredRegistrationData.map((data) => ({
        "Partner Code": data?.Partner_Code || "-",
        "Partner Name": data?.Partner_Name || "-",
        Feedback: data?.Feedback || "-",
        Comments: data?.Nps_Comments || "-",
        "Submitted By": data?.Submitted_By || "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "NPS Feedback");

      worksheet["!cols"] = [
        { wch: 15 },
        { wch: 25 },
        { wch: 30 },
        { wch: 35 },
        { wch: 15 },
      ];

      const monthName = new Date(0, parseInt(selectedMonth) - 1).toLocaleString(
        "default",
        { month: "long" },
      );
      const filename = `NPS_Feedback_Report_${monthName}_${selectedYear}.xlsx`;

      XLSX.writeFile(workbook, filename);
      toast.success("Report downloaded successfully");
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setIsDownloadingReport(false);
    }
  };

  return (
    <CommonContainer heading="NPS Feedback Report">
      <div
        className="d-flex flex-column flex-lg-row mb-2 date-search-content gap-2 search-sm"
        style={{ justifyContent: "space-between" }}
      >
        <div className="d-flex flex-column flex-md-row gap-2">
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setCurrentPage(1);
              }}
            >
              {Array.from({ length: 12 }, (_, i) => {
                const monthValue = (i + 1).toString().padStart(2, "0");

                if (
                  parseInt(selectedYear) === today.getFullYear() &&
                  parseInt(monthValue) > today.getMonth() + 1
                ) {
                  return null;
                }

                return (
                  <option key={i} value={monthValue}>
                    {new Date(0, i).toLocaleString("default", {
                      month: "long",
                    })}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select"
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setCurrentPage(1);
              }}
            >
              {Array.from({ length: 3 }, (_, i) => {
                const year = today.getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn custom-sumbitbtn d-flex align-items-center justify-content-center gap-2"
              onClick={handleDownloadReport}
              title="Download Report"
            >
              {isDownloadingReport ? (
                <>
                  <FaSpinner className="w-5 h-5 fa-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Download Report
                </>
              )}
            </button>
          </div>
        </div>

        <div className="search-box" style={{ position: "relative" }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearch}
            style={{
              paddingRight: "35px",
              borderRadius: "5px",
              border: "1px solid #ddd",
            }}
          />
          <button
            className="btn custom-sumbitbtn d-flex align-items-center justify-content-center search-box-btn"
            style={{
              padding: "5px",
              position: "absolute",
              right: "0px",
              height: "35px",
              width: "45px",
              top: "50%",
              borderTopLeftRadius: "0",
              borderBottomLeftRadius: "0",
              transform: "translateY(-45%)",
            }}
            onClick={() => setCurrentPage(1)}
          >
            <FaSearch />
          </button>
        </div>
      </div>

      <div className="mt-3 mt-lg-2">
        <div className="table-responsive mhl-registration-report-table">
          <table className="table table-bordered custom-table">
            <thead>
              <tr>
                <th>Partner Details</th>
                {/* <th>Partner Name</th> */}
                <th>Org SBU</th>
                <th>Payment Type</th>
                <th>Feedback</th>
                <th>Comments</th>
                <th>Submitted By</th>
                <th>Created On</th>
                {/* <th>Created By</th> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-2">
                    <FaSpinner size={18} color="green" className="fa-spin" />
                  </td>
                </tr>
              ) : currentRegistrationData?.length > 0 ? (
                currentRegistrationData?.map((data, index) => (
                  <tr key={index} className="patient-list-row">
                    <td
                      style={{
                        textAlign: "left",
                        verticalAlign: "middle",
                        paddingLeft: "15px",
                      }}
                    >
                      <Popover
                        content={
                          <div style={{ textAlign: "left" }}>
                            <p>
                              <strong>Payment Type:</strong> {data.Payment_Type}
                            </p>
                            <p>
                              <strong>Partner Code:</strong> {data.Partner_Code}
                            </p>
                            <p>
                              <strong>Partner Name:</strong> {data.Partner_Name}
                            </p>
                            <p>
                              <strong>Created Date:</strong>{" "}
                              {formatVisitDate(data.CreatedOn)}
                            </p>
                          </div>
                        }
                      >
                        {data.Payment_Type === "Rolling Advance" && (
                          <>
                            <i className="fas fa-battery-full text-success" />{" "}
                          </>
                        )}
                        {data.Payment_Type === "Credit" && (
                          <>
                            <i className="fas fa-credit-card text-success" />{" "}
                          </>
                        )}
                        {data.Payment_Type === "Cash" && (
                          <>
                            <i className="fas fa-money-bill-wave text-warning" />
                          </>
                        )}
                        &nbsp;
                      </Popover>
                      <b>{data.Partner_Code}</b> -{" "}
                      {data.Partner_Name.length > 25
                        ? `${data.Partner_Name.substring(0, 25)}...`
                        : data.Partner_Name}
                    </td>
                    <td>{data?.Org_Sbu || "-"}</td>
                    <td>{data?.Payment_Type || "-"}</td>
                    <td style={{textTransform: "capitalize"}}>
                      {data?.Feedback || "-"}
                    </td>
                    <td
                      style={{
                        verticalAlign: "middle",
                        textTransform: "capitalize",
                      }}
                    >
                      {data?.Nps_Comments ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <Popover
                            content={
                              <div style={{ maxWidth: "250px" }}>
                                <strong>Comment:</strong>
                                <p
                                  style={{
                                    margin: 0,
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {data?.Nps_Comments}
                                </p>
                              </div>
                            }
                          >
                            {data.Nps_Comments.length > 25 && (
                              <FaInfoCircle
                                size={13}
                                color="green"
                                style={{ cursor: "pointer" }}
                                className="me-1"
                              />
                            )}
                          </Popover>

                          <span>
                            {data.Nps_Comments.length > 25
                              ? `${data.Nps_Comments.substring(0, 25)}...`
                              : data.Nps_Comments}
                          </span>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      style={{
                        verticalAlign: "middle",
                        textTransform: "capitalize",
                      }}
                    >
                      {data?.Submitted_By ? (
                        <div className="d-flex align-items-center ms-1">
                          <Popover
                            content={
                              <div style={{ maxWidth: "250px" }}>
                                <strong>Submitted By:</strong>
                                <p
                                  style={{
                                    margin: 0,
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {data?.Submitted_By}
                                </p>
                              </div>
                            }
                          >
                            {data.Submitted_By.length > 25 && (
                              <FaInfoCircle
                                size={13}
                                color="green"
                                style={{ cursor: "pointer" }}
                                className="me-1"
                              />
                            )}
                          </Popover>

                          <span>
                            {data.Submitted_By.length > 25
                              ? `${data.Submitted_By.substring(0, 25)}...`
                              : data.Submitted_By}
                          </span>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {formatDate(data?.CreatedOn) || "-"}
                    </td>
                    {/* <td>{data?.CreatedBy || "-"}</td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">
                    No Data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredRegistrationData.length > itemsPerPage && (
          <CommonPagination
            pageList={filteredRegistrationData}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </CommonContainer>
  );
};

export default NPSFeedbackReport;
